import React, { useState } from 'react';
import { ShieldCheck, Users, Landmark, TrendingUp, Smartphone, Eye, X, Calendar, MapPin, Award as Trophy } from 'lucide-react';

export default function Gallery() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);

  const categories = ['All', 'Branches', 'Events & Meets', 'Community & CSR', 'Digital Banking'];

  const galleryItems = [
    {
      id: 1,
      category: 'Branches',
      title: 'Sangli Main Branch Office',
      desc: 'Our newly renovated main branch office equipped with modern customer service desks and secure vaults.',
      image: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&q=80&w=800',
      date: 'Jan 2026',
      location: 'Sangli, MH'
    },
    {
      id: 2,
      category: 'Events & Meets',
      title: 'Annual General Body Meeting',
      desc: 'Members and directors gathered at our annual assembly discussing democratic financial policies and community progress.',
      image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&q=80&w=800',
      date: 'Dec 2025',
      location: 'Sangli Corporate Hall'
    },
    {
      id: 3,
      category: 'Community & CSR',
      title: 'Farmer Empowerment Seminar',
      desc: 'Free training seminar organized for 250+ farmer members on sustainable crop yield methods and agricultural credit benefits.',
      image: 'https://images.unsplash.com/photo-1592417817098-8f3d6eb19675?auto=format&fit=crop&q=80&w=800',
      date: 'Feb 2026',
      location: 'Miraj Block'
    },
    {
      id: 4,
      category: 'Digital Banking',
      title: 'Core Banking Software Integration',
      desc: 'Centralized server racks housing secure, high-grade database software for real-time ledger accounting.',
      image: 'https://images.unsplash.com/photo-1563013544-824ae1d704d3?auto=format&fit=crop&q=80&w=800',
      date: 'Nov 2025',
      location: 'IT Operations HQ'
    },
    {
      id: 5,
      category: 'Branches',
      title: 'Kolhapur Premium Service Counter',
      desc: 'Welcoming customer desks at our Kolhapur branch offering seamless deposits and gold credit disbursements.',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80&w=800',
      date: 'Mar 2026',
      location: 'Kolhapur, MH'
    },
    {
      id: 6,
      category: 'Community & CSR',
      title: 'Rural Merchant Workshop',
      desc: 'Supporting local weavers and small merchants with simplified MSME micro-credits and business growth advice.',
      image: 'https://images.unsplash.com/photo-1509099836639-18ba1795216d?auto=format&fit=crop&q=80&w=800',
      date: 'Oct 2025',
      location: 'Vita Village'
    },
    {
      id: 7,
      category: 'Events & Meets',
      title: 'Cooperative Union Summit Award',
      desc: 'Sahyadri board directors receiving recognition for excellent performance and community integration.',
      image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&q=80&w=800',
      date: 'Aug 2025',
      location: 'Mumbai Corporate Center'
    },
    {
      id: 8,
      category: 'Digital Banking',
      title: 'Vanguard Cyber Security Audits',
      desc: 'Regular vulnerability checkups and server scans ensuring complete safety for online member portal transactions.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800',
      date: 'May 2026',
      location: 'Security Operations Hub'
    }
  ];

  const achievements = [
    {
      title: "Grade 'A' Audit Classification",
      subtitle: "State Cooperative Department",
      desc: "Consistently awarded the prestigious Grade 'A' Audit Rating by the cooperative department auditor office, Sangli, for pristine financial balance sheets and clean management logs.",
      metric: "Grade A+",
      icon: ShieldCheck,
      color: "bg-emerald/10 text-emerald"
    },
    {
      title: "Cooperative Innovator Award",
      subtitle: "Apex Cooperative Union Summit",
      desc: "Honored with the best cooperative innovator award for integrating centralized digital core banking, SMS verification, and instant UPI transaction channels.",
      metric: "Best Tech",
      icon: Trophy,
      color: "bg-[#B31825]/10 text-[#B31825]"
    },
    {
      title: "₹340+ Crores Total Deposits",
      subtitle: "Secured Member Growth",
      desc: "Demonstrating high financial security and strong bond of trust with over 18,500+ retail accounts, merchants, and farmers investing in term deposit portfolios.",
      metric: "₹340 Cr+",
      icon: TrendingUp,
      color: "bg-blue-600/10 text-blue-600"
    },
    {
      title: "Rural Development Leadership",
      subtitle: "Agricultural Outreach Scheme",
      desc: "Recognized for providing micro finance, gold credits, and crop loans to over 12,000+ local families, helping them avoid high interest private money lending traps.",
      metric: "12,000+ Credits",
      icon: Users,
      color: "bg-purple-600/10 text-purple-600"
    },
    {
      title: "Multi-Branch Network expansion",
      subtitle: "Expanding Regional Footprint",
      desc: "Successfully operating fully computerized branches across Sangli, Miraj, Vita, Tasgaon, Kolhapur, and Pune, providing unified access to deposit accounts.",
      metric: "6 Branches",
      icon: Landmark,
      color: "bg-amber-600/10 text-amber-600"
    },
    {
      title: "Next-Gen Portal Safety",
      subtitle: "Vanguard Guard Security",
      desc: "Setting up automated fraud monitoring triggers, Aadhaar card KYC verification routing, and multi-factor portal logins protecting credit approvals.",
      metric: "100% Safe",
      icon: Smartphone,
      color: "bg-teal-600/10 text-teal-600"
    }
  ];

  const filteredItems = galleryItems.filter(item => 
    activeCategory === 'All' || item.category === activeCategory
  );

  return (
    <div className="pt-[68px] font-sans bg-[#F7F9FC]">
      {/* Header Banner */}
      <section 
        className="relative overflow-hidden text-navy py-20 px-4 text-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/page header.png')" }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(0,0,0,0.01),transparent)] pointer-events-none" />
        <div className="max-w-4xl mx-auto space-y-4 relative z-10">
          <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">Institution Gallery</span>
          <h1 className="font-montserrat font-extrabold text-3xl sm:text-4xl text-navy">Visualizing Our Journey & Milestones</h1>
          <p className="text-slate-600 text-sm sm:text-base font-normal max-w-2xl mx-auto">
            Explore our rich cooperative heritage, modern branches, community development programs, and achievements that define Sahyadri Urban.
          </p>
        </div>
      </section>

      {/* SECTION 1: Gallery Images Grid */}
      <section className="py-20 text-navy max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12 space-y-3">
          <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">Visual Highlights</span>
          <h2 className="font-montserrat font-extrabold text-2xl sm:text-3xl text-navy">Photo Gallery</h2>
          <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">
            Take a look at our daily operations, secure infrastructure, rural farmer meets, and collaborative team summits.
          </p>
        </div>

        {/* Category Filter Tabs */}
        <div className="flex flex-wrap gap-2 justify-center mb-12 select-none">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2.5 rounded-xl font-montserrat font-bold text-xs transition-all border ${
                activeCategory === cat 
                  ? 'bg-[#B31825] border-[#B31825] text-white shadow-md' 
                  : 'bg-white border-gray-250 text-navy hover:bg-gray-50'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {filteredItems.map((item) => (
            <div 
              key={item.id} 
              className="group bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col cursor-pointer"
              onClick={() => setSelectedImage(item)}
            >
              {/* Image Container */}
              <div className="relative overflow-hidden aspect-[4/3] bg-gray-100">
                <img 
                  src={item.image} 
                  alt={item.title} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-between p-4">
                  <span className="text-white text-xs font-semibold bg-[#B31825] px-2.5 py-1 rounded-lg">
                    {item.category}
                  </span>
                  <div className="bg-white/20 backdrop-blur-md text-white p-2 rounded-xl">
                    <Eye className="h-4 w-4" />
                  </div>
                </div>
              </div>

              {/* Text Info */}
              <div className="p-5 flex-grow flex flex-col justify-between">
                <div className="space-y-2">
                  <h3 className="font-montserrat font-bold text-sm text-navy group-hover:text-[#B31825] transition-colors leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-gray-500 text-[11px] leading-relaxed line-clamp-2 font-light">
                    {item.desc}
                  </p>
                </div>
                <div className="flex items-center justify-between text-[10px] text-gray-400 font-semibold pt-4 mt-4 border-t border-gray-100">
                  <span className="flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    {item.date}
                  </span>
                  <span className="flex items-center">
                    <MapPin className="h-3 w-3 mr-1" />
                    {item.location}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 2: Achievements Section */}
      <section className="py-24 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
            <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">Certificates & Merits</span>
            <h2 className="font-montserrat font-extrabold text-2xl sm:text-3xl text-navy">Our Awards & Achievements</h2>
            <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">
              Our consistent audit success, technological breakthroughs, and cooperative welfare impact are verified and appreciated at regional and state levels.
            </p>
          </div>

          {/* Achievements Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {achievements.map((item, i) => {
              const IconComp = item.icon;
              return (
                <div 
                  key={i} 
                  className="bg-[#F8FAFC] border border-gray-200 rounded-3xl p-6 sm:p-8 hover:bg-white hover:border-[#B31825] hover:shadow-xl transition-all duration-300 flex flex-col justify-between space-y-6 group"
                >
                  <div className="space-y-4">
                    {/* Badge & Icon Row */}
                    <div className="flex items-center justify-between">
                      <div className={`p-3.5 rounded-2xl ${item.color} group-hover:scale-105 transition-transform`}>
                        <IconComp className="h-6 w-6" />
                      </div>
                      <span className="text-[10px] font-bold tracking-widest bg-gray-100 text-[#052046] px-3 py-1.5 rounded-full uppercase">
                        {item.metric}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="space-y-1">
                      <h4 className="font-montserrat font-extrabold text-base text-navy leading-snug group-hover:text-[#B31825] transition-colors">
                        {item.title}
                      </h4>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                        {item.subtitle}
                      </p>
                    </div>

                    <p className="text-gray-500 text-xs sm:text-sm leading-relaxed font-light">
                      {item.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lightbox / Image Zoom Modal Popup */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-fade-in"
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className="bg-white rounded-3xl max-w-3xl w-full overflow-hidden shadow-2xl relative animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button 
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-colors z-10"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Modal Body */}
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Image Side */}
              <div className="bg-black aspect-video md:aspect-square flex items-center">
                <img 
                  src={selectedImage.image} 
                  alt={selectedImage.title} 
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Text Side */}
              <div className="p-8 flex flex-col justify-between text-navy space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-bold text-[#B31825] bg-[#B31825]/5 px-2.5 py-1 rounded-lg uppercase tracking-wider">
                      {selectedImage.category}
                    </span>
                  </div>
                  <h3 className="font-montserrat font-extrabold text-xl leading-snug">
                    {selectedImage.title}
                  </h3>
                  <p className="text-gray-500 text-xs sm:text-sm leading-relaxed font-light">
                    {selectedImage.desc}
                  </p>
                </div>

                <div className="flex items-center justify-between text-[11px] text-gray-400 font-semibold pt-6 border-t border-gray-150">
                  <span className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1.5 text-gray-400" />
                    {selectedImage.date}
                  </span>
                  <span className="flex items-center">
                    <MapPin className="h-4 w-4 mr-1.5 text-gray-400" />
                    {selectedImage.location}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
